import React, { useEffect, useState, useCallback } from 'react';
import { discussionAPI } from '../../services/api';
import PostComposer from './PostComposer';

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

function initials(name) {
  if (!name) return '?';
  return name
    .split(' ')
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

export default function DiscussionTab({ sceneId, tutorialId, domain }) {
  const discussionKey = tutorialId || sceneId;
  const [posts, setPosts] = useState([]);
  const [total, setTotal] = useState(0);
  const [sort, setSort] = useState('newest');
  const [loading, setLoading] = useState(true);
  const [expandedThread, setExpandedThread] = useState(null);
  const [replies, setReplies] = useState({});
  const [showComposer, setShowComposer] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [collapsed, setCollapsed] = useState(true);

  const fetchPosts = useCallback(() => {
    if (!discussionKey) return;
    setLoading(true);
    discussionAPI
      .listThreads(discussionKey, sort)
      .then((r) => {
        setPosts(r.data?.threads || r.data?.posts || []);
        setTotal(r.data?.total || 0);
      })
      .catch(() => setPosts([]))
      .finally(() => setLoading(false));
  }, [discussionKey, sort]);

  useEffect(() => { fetchPosts(); }, [fetchPosts]);

  const handleExpand = (threadId) => {
    if (expandedThread === threadId) {
      setExpandedThread(null);
      return;
    }
    setExpandedThread(threadId);
    if (!replies[threadId]) {
      discussionAPI
        .getReplies(threadId)
        .then((r) =>
          setReplies((prev) => ({ ...prev, [threadId]: r.data?.thread?.replies || r.data?.replies || [] }))
        )
        .catch(() => {});
    }
  };

  const handleUpvote = (postId) => {
    discussionAPI
      .upvote(postId)
      .then(() => fetchPosts())
      .catch(() => {});
  };

  const handlePostCreated = () => {
    setShowComposer(false);
    setReplyTo(null);
    fetchPosts();
    if (expandedThread) {
      discussionAPI
        .getReplies(expandedThread)
        .then((r) =>
          setReplies((prev) => ({ ...prev, [expandedThread]: r.data?.thread?.replies || r.data?.replies || [] }))
        )
        .catch(() => {});
    }
  };

  return (
    <div style={containerStyle}>
      <button onClick={() => setCollapsed((v) => !v)} style={toggleBtnStyle}>
        <span style={{ fontSize: 15 }}>💬</span>
        <span style={{ fontWeight: 600 }}>Discussion ({total})</span>
        <span style={{ color: '#94a3b8', fontSize: 12 }}>{collapsed ? '▸' : '▾'}</span>
      </button>

      {!collapsed && (
        <div style={{ paddingTop: 12 }}>
          <div style={toolbarStyle}>
            <button
              onClick={() => setShowComposer(true)}
              style={newPostBtnStyle}
            >
              + New Post
            </button>
            <div style={{ display: 'flex', gap: 4 }}>
              {[{ key: 'newest', label: 'Recent' }, { key: 'upvotes', label: 'Popular' }].map((s) => (
                <button
                  key={s.key}
                  onClick={() => setSort(s.key)}
                  style={sortBtnStyle(sort === s.key)}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          {showComposer && !replyTo && (
            <PostComposer
              sceneId={discussionKey}
              onPostCreated={handlePostCreated}
              onCancel={() => setShowComposer(false)}
            />
          )}

          {loading && <div style={mutedStyle}>Loading discussions…</div>}

          {!loading && posts.length === 0 && (
            <div style={emptyStyle}>
              No discussions yet. Be the first to start a conversation!
            </div>
          )}

          {posts.map((post) => (
            <div key={post.post_id} style={postCardStyle}>
              <div style={postHeaderStyle}>
                <div style={avatarStyle}>{initials(post.author_name || post.display_name)}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontWeight: 600, fontSize: 14, color: '#1e293b' }}>
                      {post.author_name || post.display_name}
                    </span>
                    <span style={{ fontSize: 12, color: '#94a3b8' }}>
                      {timeAgo(post.created_at)}
                    </span>
                    {post.edited_at && (
                      <span style={{ fontSize: 11, color: '#94a3b8', fontStyle: 'italic' }}>
                        (edited)
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div style={bodyStyle}>{post.body_md}</div>

              {post.visibility === 'pending' && (
                <div style={pendingBadgeStyle}>Pending moderation</div>
              )}

              <div style={actionsStyle}>
                <button
                  onClick={() => handleUpvote(post.post_id)}
                  style={actionBtnStyle}
                  title="Upvote"
                >
                  ▲ {post.upvotes || 0}
                </button>
                <button
                  onClick={() => handleExpand(post.post_id)}
                  style={actionBtnStyle}
                >
                  💬 {post.reply_count || 0} {post.reply_count === 1 ? 'reply' : 'replies'}
                </button>
                <button
                  onClick={() => {
                    setReplyTo(post);
                    setExpandedThread(post.post_id);
                  }}
                  style={actionBtnStyle}
                >
                  Reply
                </button>
              </div>

              {expandedThread === post.post_id && (
                <div style={repliesContainerStyle}>
                  {replyTo?.post_id === post.post_id && (
                    <PostComposer
                      sceneId={discussionKey}
                      parentId={post.post_id}
                      onPostCreated={handlePostCreated}
                      onCancel={() => setReplyTo(null)}
                      placeholder="Write a reply…"
                    />
                  )}
                  {(replies[post.post_id] || []).map((reply) => (
                    <div key={reply.post_id} style={replyCardStyle}>
                      <div style={postHeaderStyle}>
                        <div style={{ ...avatarStyle, width: 28, height: 28, fontSize: 11 }}>
                          {initials(reply.author_name || reply.display_name)}
                        </div>
                        <div style={{ flex: 1 }}>
                          <span style={{ fontWeight: 600, fontSize: 13, color: '#1e293b' }}>
                            {reply.author_name || reply.display_name}
                          </span>
                          <span style={{ fontSize: 12, color: '#94a3b8', marginLeft: 8 }}>
                            {timeAgo(reply.created_at)}
                          </span>
                        </div>
                      </div>
                      <div style={{ ...bodyStyle, fontSize: 13 }}>{reply.body_md}</div>
                      <div style={actionsStyle}>
                        <button
                          onClick={() => handleUpvote(reply.post_id)}
                          style={actionBtnStyle}
                        >
                          ▲ {reply.upvotes || 0}
                        </button>
                      </div>
                    </div>
                  ))}
                  {(replies[post.post_id] || []).length === 0 && !replyTo && (
                    <div style={{ fontSize: 13, color: '#94a3b8', padding: '8px 0' }}>
                      No replies yet.
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const containerStyle = {
  marginTop: 24,
  border: '1px solid #e2e8f0',
  borderRadius: 12,
  padding: '12px 16px',
  background: '#fafbfc',
};
const toggleBtnStyle = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  fontSize: 15,
  color: '#334155',
  padding: 0,
  width: '100%',
};
const toolbarStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: 12,
};
const newPostBtnStyle = {
  background: '#3b82f6',
  color: '#fff',
  border: 'none',
  borderRadius: 6,
  padding: '6px 14px',
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
};
const sortBtnStyle = (active) => ({
  background: active ? '#1e293b' : '#e2e8f0',
  color: active ? '#fff' : '#475569',
  border: 'none',
  borderRadius: 4,
  padding: '4px 10px',
  fontSize: 12,
  fontWeight: 500,
  cursor: 'pointer',
});
const mutedStyle = { color: '#94a3b8', fontSize: 13, padding: '16px 0' };
const emptyStyle = {
  color: '#94a3b8',
  fontSize: 14,
  padding: '24px 0',
  textAlign: 'center',
};
const postCardStyle = {
  background: '#fff',
  border: '1px solid #e5e7eb',
  borderRadius: 10,
  padding: 14,
  marginBottom: 10,
};
const postHeaderStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  marginBottom: 8,
};
const avatarStyle = {
  width: 34,
  height: 34,
  borderRadius: '50%',
  background: 'linear-gradient(135deg, #3b82f6, #6366f1)',
  color: '#fff',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 13,
  fontWeight: 700,
  flexShrink: 0,
};
const bodyStyle = {
  fontSize: 14,
  lineHeight: 1.6,
  color: '#1f2937',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
};
const pendingBadgeStyle = {
  display: 'inline-block',
  marginTop: 6,
  fontSize: 11,
  fontWeight: 600,
  color: '#92400e',
  background: '#fef3c7',
  padding: '2px 8px',
  borderRadius: 999,
};
const actionsStyle = {
  display: 'flex',
  gap: 12,
  marginTop: 8,
  alignItems: 'center',
};
const actionBtnStyle = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  fontSize: 12,
  color: '#64748b',
  padding: '2px 0',
  fontWeight: 500,
};
const repliesContainerStyle = {
  borderTop: '1px solid #f1f5f9',
  marginTop: 10,
  paddingTop: 10,
  paddingLeft: 16,
};
const replyCardStyle = {
  background: '#f8fafc',
  borderRadius: 8,
  padding: 10,
  marginBottom: 6,
  border: '1px solid #f1f5f9',
};
